<!--AddTextLimitModal.vue-->
<script setup>
import { computed, ref, watch, nextTick } from "vue";
import AccessDeniedModal from "@/components/User/AccessDeniedModal.vue";
import { useUserStore } from "@/stores/useUserStore";

const userStore = useUserStore();

const showAccessDenied = ref(false);

const props = defineProps({
  show: Boolean,
  info: {
    type: Object,
    default: () => ({}),
  },
  entityType: {
    type: String,
    required: false,
    default: "",
  },
});

const emit = defineEmits(["close", "open-plans"]);

const CHARS_PER_PAGE = 2450;

// ================== ESC / FOCO ==================
const wrapperRef = ref(null);

watch(
  () => props.show,
  (isOpen) => {
    if (isOpen) {
      nextTick(() => {
        wrapperRef.value?.focus();
      });
    }
  }
);

// ------------------- HELPERS -------------------

function extractNumberBetween(message, start, end) {
  if (!message) return null;

  const startIndex = message.indexOf(start);
  if (startIndex === -1) return null;

  const from = startIndex + start.length;
  const endIndex = message.indexOf(end, from);
  if (endIndex === -1) return null;

  const segment = message.substring(from, endIndex);
  const match = segment.match(/(\d+)/);

  return match ? Number(match[1]) : null;
}

// normaliza entityType vindo de props ou de info
const normalizedEntityType = computed(() =>
  (props.entityType || props.info?.entityType || "").toLowerCase()
);

const isUsersType = computed(() => normalizedEntityType.value === "usuários");

// ------------------- PARSE A PARTIR DAS MENSAGENS -------------------

const parsedValues = computed(() => {
  const message = props.info?.message || "";
  const isUsers = isUsersType.value;

  if (!message) {
    return { limit: null, currentUsage: null, newUsage: null };
  }

  if (isUsers) {
    // 1) Quando entityType == "usuários"
    const limit = extractNumberBetween(
      message,
      "associar o total de",
      "pessoas"
    );

    const currentUsage = extractNumberBetween(
      message,
      "tinha associada",
      "e agora tentou"
    );

    const newUsage = extractNumberBetween(
      message,
      "adicionar",
      ", excedendo o limite"
    );

    return { limit, currentUsage, newUsage };
  } else {
    // 2) Quando entityType != "usuários"
    const limit = extractNumberBetween(
      message,
      "permite o total de",
      "caracteres"
    );

    const currentUsage = extractNumberBetween(
      message,
      "foram usados",
      ". Logo"
    );

    const newUsage = extractNumberBetween(
      message,
      "tentou incluir",
      " caracteres."
    );

    return { limit, currentUsage, newUsage };
  }
});

// ------------------- COMPUTEDS -------------------

const limit = computed(() => parsedValues.value.limit);
const currentUsage = computed(() => parsedValues.value.currentUsage);
const newUsage = computed(() => parsedValues.value.newUsage);

const remainingBalance = computed(() => {
  if (limit.value == null || currentUsage.value == null) return null;
  return limit.value - currentUsage.value;
});

const excessUsage = computed(() => {
  if (remainingBalance.value == null || newUsage.value == null) return null;
  return Math.max(0, newUsage.value - remainingBalance.value);
});

const formatNumber = (value) => {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat("pt-BR").format(value);
};

const formatPages = (chars) => {
  if (chars === null || chars === undefined) return "-";

  const pages = chars / CHARS_PER_PAGE;

  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(pages);
};

// ------------------- AÇÕES -------------------

function handleClose() {
  emit("close");
}

function handleOpenPlans() {
  // ✅ NOVO: membro não pode alterar plano — exibe aviso de acesso restrito
  if (userStore.currentUser?.loginContext === "managed") {
    showAccessDenied.value = true;
    return;
  }
  emit("open-plans");
}
</script>

<template>
  <teleport to="body">
    <div
      v-if="show"
      ref="wrapperRef"
      class="fixed inset-0 z-[10000] flex items-start justify-center bg-black/20 pt-16 sm:pt-20"
      tabindex="0"
      @keydown.esc.stop.prevent="handleClose"
    >
      <div
        class="w-full max-w-lg rounded-2xl bg-surface px-6 py-5 shadow-2xl text-[15px] sm:text-base"
        @click.stop
      >
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-lg sm:text-xl font-semibold text-slate-900">
            Limite do plano atingido
          </h2>
          <button
            type="button"
            class="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition"
            @click="handleClose"
          >
            ✕
          </button>
        </div>

        <div
          class="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-[15px] sm:text-base text-yellow-900 leading-relaxed text-justify"
        >
          <p class="font-semibold mb-2">
            Você atingiu o limite de
            {{ entityType || info?.entityType || "itens" }} do seu plano.
          </p>

          <p class="mb-3" v-if="isUsersType && limit !== null">
            Seu plano permite associar até
            <strong>{{ limit }}</strong> pessoas em grupos. Você já tinha
            {{ currentUsage > 1 ? "associadas" : "associada" }}
            <strong>{{ currentUsage }}</strong>
            e agora tentou adicionar
            <strong>{{ newUsage }}</strong>
            {{ newUsage > 1 ? "novas pessoas" : "nova pessoa" }}, excedendo o
            limite do seu plano.

            <span v-if="limit - currentUsage > 0">
              No momento, você só pode incluir mais
              {{ limit - currentUsage }}
              {{ limit - currentUsage > 1 ? "pessoas" : "pessoa" }}.
            </span>
          </p>

          <p class="mb-3" v-else-if="limit !== null">
            Seu plano permite o total de
            <strong>{{ formatNumber(limit) }}</strong>
            caracteres em texto, o equivalente a aproximadamente
            <b>{{ formatPages(limit) }}</b> páginas
          </p>

          <div
            v-if="
              !isUsersType &&
              remainingBalance !== null &&
              newUsage !== null &&
              excessUsage !== null
            "
            class="overflow-x-auto mb-3"
          >
            <table class="min-w-full text-sm border-collapse">
              <thead>
                <tr
                  class="bg-yellow-100/70 border-b border-yellow-300 text-yellow-800"
                >
                  <th class="py-2 px-1 sm:px-3 text-left font-medium w-1/3">
                    Detalhamento
                  </th>
                  <th class="py-2 px-1 sm:px-3 text-right font-medium w-1/3">
                    Caracteres
                  </th>
                  <th class="py-2 px-1 sm:px-3 text-right font-medium w-1/3">
                    Equivalente em páginas
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-yellow-200">
                <tr class="text-yellow-900">
                  <td class="py-1 px-1 sm:px-3 text-left">Saldo atual</td>
                  <td class="py-1 px-1 sm:px-3 text-right font-bold">
                    {{ formatNumber(remainingBalance) }}
                  </td>
                  <td class="py-1 px-1 sm:px-3 text-right">
                    {{ formatPages(remainingBalance) }}
                  </td>
                </tr>

                <tr class="text-yellow-900">
                  <td class="py-1 px-1 sm:px-3 text-left">Solicitou agora</td>
                  <td class="py-1 px-1 sm:px-3 text-right font-bold">
                    {{ formatNumber(newUsage) }}
                  </td>
                  <td class="py-1 px-1 sm:px-3 text-right">
                    {{ formatPages(newUsage) }}
                  </td>
                </tr>

                <tr class="text-yellow-900">
                  <td class="py-1 px-1 sm:px-3 text-left">Excesso</td>
                  <td
                    class="py-1 px-1 sm:px-3 text-right font-bold text-red-700"
                  >
                    {{ formatNumber(excessUsage) }}
                  </td>
                  <td class="py-1 px-1 sm:px-3 text-right">
                    {{ formatPages(excessUsage) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p class="mb-2" v-if="!limit && !currentUsage && !newUsage">
            {{ info?.message || "A operação excede o limite do seu plano." }}
          </p>

          <p class="mt-1">
            <span v-if="userStore.currentUser?.loginContext === 'managed'">
              Para incluir mais, o <b>titular</b> deve atualizar o plano.
            </span>
            <span v-else>Para incluir mais, atualize seu plano clicando no botão abaixo.</span>
          </p>
        </div>

        <div class="mt-6 flex justify-end gap-3">
          <button @click="handleClose" class="btn btn-secondary text-sm">
            Fechar
          </button>

          <button
            v-if="userStore.currentUser?.loginContext !== 'managed'"
            @click="handleOpenPlans"
            class="btn bg-[var(--brand-light)] hover:bg-[var(--brand-light-hover)] text-[color:var(--text-on-brand)] flex items-center gap-2 text-sm font-semibold"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M13 7h6m0 0v6m0-6L10 16"
              />
            </svg>

            Atualizar plano
          </button>
        </div>
      </div>
    </div>

    <AccessDeniedModal
      :show="showAccessDenied"
      @close="showAccessDenied = false"
    />
  </teleport>
</template>


